import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, useScroll, useTransform, useReducedMotion } from 'motion/react';
import { useUiStore } from '@/store/useUiStore';
import WordReveal from '@/components/WordReveal';
import './Ch1Leadership.css';

interface QuoteProps {
  quoteKey: 'quote1' | 'quote2';
  portrait: string;
  index: number;
}

function QuotePanel({ quoteKey, portrait, index }: QuoteProps) {
  const { t } = useTranslation();
  const reduce = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });
  // parallax on the oversized quote mark + portrait
  const markY = useTransform(scrollYProgress, [0, 1], reduce ? [0, 0] : [60, -60]);
  const portraitY = useTransform(scrollYProgress, [0, 1], reduce ? [0, 0] : [40, -40]);
  const flip = index % 2 === 1;

  return (
    <div ref={ref} className={`quote-panel${flip ? ' quote-panel--flip' : ''}`}>
      <div className="quote-body">
        <motion.span className="quote-mark" style={{ y: markY }} aria-hidden="true">
          &#8220;
        </motion.span>
        <WordReveal as="blockquote" className="quote-text" text={t(`leadership.${quoteKey}.text`)} />
        <div className="quote-author">
          <span className="quote-author-name">{t(`leadership.${quoteKey}.author`)}</span>
          <span className="quote-author-role">{t(`leadership.${quoteKey}.role`)}</span>
        </div>
      </div>
      <motion.div className="quote-portrait" style={{ y: portraitY }}>
        <img src={portrait} alt={t(`leadership.${quoteKey}.author`)} loading="lazy" />
        <span className="quote-portrait-glow" aria-hidden="true" />
      </motion.div>
    </div>
  );
}

const PORTRAITS: Record<'quote1' | 'quote2', string> = {
  quote1: '/portraits/king-salman.jpg',
  quote2: '/portraits/crown-prince.jpg',
};

interface Props {
  /** which royal quotes to show, in order (default: both) */
  quotes?: Array<'quote1' | 'quote2'>;
}

export default function Ch1Leadership({ quotes = ['quote1', 'quote2'] }: Props) {
  const { t } = useTranslation();
  const setActiveChapter = useUiStore((s) => s.setActiveChapter);
  const ref = useRef<HTMLElement>(null);

  return (
    <section
      ref={ref}
      id="leadership"
      className="chapter chapter-leadership"
      aria-labelledby="leadership-title"
      onMouseEnter={() => setActiveChapter('leadership')}
    >
      <div className="leadership-inner">
        <p className="section-kicker">{t('leadership.kicker')}</p>
        <h2 id="leadership-title" className="sr-only">
          {t('nav.chapters.leadership')}
        </h2>
        {quotes.map((q, i) => (
          <QuotePanel key={q} quoteKey={q} portrait={PORTRAITS[q]} index={i} />
        ))}
      </div>
    </section>
  );
}
